<script setup>
import { onMounted, onUnmounted, defineProps, defineEmits, ref, watch, computed } from "vue";
import { tns } from "tiny-slider";

const props = defineProps({
    currentSlide: {
        type: Number,
        required: true
    },
    elementId: {
        type: [Number, String],
        required: true
    },
    slides: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(["change", "update:currentSlide"]);

const mainContainer = ref(null);
const controlsContainer = ref(null);
const thumbsContainer = ref(null);

const mainSlider = ref(null);
const thumbsSlider = ref(null);

const slideCount = computed(() => props.slides.length || 1);
const currentIndex = ref(Math.max(1, props.currentSlide || 1));

const updateFromSlider = (info) => {
    const next = Math.min(info.displayIndex || info.index + 1, slideCount.value);
    if (next === currentIndex.value) return;
    currentIndex.value = next;
    emit("change", next);
    emit("update:currentSlide", next);
    thumbsSlider.value?.goTo(next - 1);
};

const goTo = (displayIdx1) => {
    const idx0 = Math.max(0, Math.min(displayIdx1 - 1, slideCount.value - 1));
    currentIndex.value = idx0 + 1;
    mainSlider.value?.goTo(idx0);
    thumbsSlider.value?.goTo(idx0);
};

onMounted(() => {
    if (!mainContainer.value) return;

    // MAIN
    const ms = tns({
        container: mainContainer.value,
        items: 1,
        startIndex: Math.max(0, (props.currentSlide || 1) - 1),
        gutter: 0,
        autoplay: false,
        loop: false,
        mouseDrag: true,
        controls: true,
        controlsContainer: controlsContainer.value || undefined,
        nav: false,
        onInit: updateFromSlider
    });
    mainSlider.value = ms;
    ms.events.on("indexChanged", updateFromSlider);

    // THUMBS 
        const ts = tns({
            container: thumbsContainer.value,
            items: 4,
            gutter: 0,
            slideBy: 1,
            loop: false,
            nav: false,
            controls: false,
            mouseDrag: true,
            autoplay: false,
        });
        thumbsSlider.value = ts;
});

watch(() => props.currentSlide, (val) => {
    if (val && val !== currentIndex.value) goTo(val);
});

onUnmounted(() => {
    mainSlider.value?.destroy?.();
    thumbsSlider.value?.destroy?.();
    mainSlider.value = null;
    thumbsSlider.value = null;
});
</script>

<template>
    <div class="relative">
        <!-- MAIN -->
        <div class="rounded-2xl overflow-hidden">
            <div :id="'slider-' + elementId" ref="mainContainer">
                <div v-for="(image, index) in slides" :key="image.id ?? index" class="relative aspect-square">
                    <img 
                        :src="image.image.main" 
                        :alt="image.alt"
                        class="object-cover size-full block"
                        :loading="index === 0 ? 'eager' : 'lazy'" 
                        :fetchpriority="index === 0 ? 'high' : null"
                        decoding="async" 
                    >
                    <button 
                        :data-src="image.image.full"
                        :data-fancybox="'product-gallery-' + elementId"
                        data-type="image"
                        class="absolute bottom-4 left-4" 
                    >
                        <kd-icon-zoom size="25" fill="black" class="drop-shadow-ts-tight hover:scale-110" />
                    </button>
                </div>
            </div>
        </div>
        <div 
            v-if="slides.length > 1" 
            :id="'slider-' + elementId + '-controls'" 
            ref="controlsContainer"
            class="relative"
        >
            <div class="absolute bottom-4 right-15 cursor-pointer">
                <kd-icon-arrow-left class="drop-shadow-ts-tight hover:scale-110" fill="gray" size="30" />
            </div>
            <div class="absolute bottom-4 right-5 cursor-pointer">
                <kd-icon-arrow-right class="drop-shadow-ts-tight hover:scale-110" fill="gray" size="30" />
            </div>
        </div>
        <!-- THUMBS -->
        <div v-if="slides.length" class="mt-3 overflow-hidden">
            <div
                ref="thumbsContainer"
                class="pl-2"
            >
                <button
                    v-for="(thumb, idx) in slides"
                    :key="thumb.id ?? idx"
                    type="button"
                    @click="goTo(idx + 1)"
                    class="relative overflow-hidden rounded-lg aspect-square p-px pr-2 pb-2"
                >
                    <img
                        :src="thumb.image.thumb"
                        :alt="thumb.alt"
                        class="size-full object-cover object-bottom block box-border rounded-[inherit]"
                        :class="{ 'border-2 border-black': currentIndex === idx + 1 }"
                    >
                </button>
            </div>
        </div>
    </div>
</template>